/* eslint-disable */
// Session 60.24 · Member-facing booking detail page.
//
// PURE PRESENTATION LAYER over the existing /api/bookings/v2/{req_id}
// endpoint. Does NOT duplicate any booking or payment logic — every state
// transition and every monetary field is authoritative on the backend
// (booking_requests, bookings, payment_transactions). This page only
// renders what the API returns and lets the member navigate back into the
// existing checkout success/cancel flows for follow-up actions.
//
// Purpose: fix the P0 booking-notification deep-link, which previously
// pointed to /account/bookings/{req_id} — an unregistered route that
// fell through the App.js catch-all to `/`. Now that page exists.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";

function fmtDateTime(d, t) {
  if (!d) return "";
  try {
    return new Date(`${d}T${t || "00:00"}:00`).toLocaleString(undefined, {
      weekday: "long", month: "long", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return `${d} · ${t}`; }
}

function fmtMoney(cents) {
  if (cents == null) return "";
  return `$${(cents / 100).toFixed(2)}`;
}

// State -> presentation copy. Every branch resolves to the same booking
// object shape from the API. Copy is release-critical for the App Store
// notifications review: every booking event (request → approve → decline →
// confirm → cancel → refund → payment) must render a clear, honest state.
function statePresentation(state) {
  const s = String(state || "").toLowerCase();
  if (s === "confirmed" || s === "fully_paid") {
    return { label: "Confirmed", eyebrow: "Confirmed", accent: "#2d7a4f",
             blurb: "You're booked. See you there." };
  }
  if (s === "pending_request") {
    return { label: "Awaiting host approval", eyebrow: "Pending", accent: "#B8913A",
             blurb: "Your payment is held while the host reviews your request." };
  }
  if (s === "pending_payment" || s === "pending_lock") {
    return { label: "Payment processing", eyebrow: "Processing", accent: "#B8913A",
             blurb: "We're confirming your payment with Stripe. This usually takes seconds." };
  }
  if (s === "declined") {
    return { label: "Declined by host", eyebrow: "Declined", accent: "#B34141",
             blurb: "The host was unable to accept this request. Your payment has been refunded in full." };
  }
  if (s === "cancelled" || s === "canceled") {
    return { label: "Cancelled", eyebrow: "Cancelled", accent: "#B34141",
             blurb: "This reservation was cancelled." };
  }
  if (s === "refunded") {
    return { label: "Refunded", eyebrow: "Refunded", accent: "#B8913A",
             blurb: "This booking was refunded. Please allow 5-10 business days to see it on your statement." };
  }
  if (s === "expired_unpaid") {
    return { label: "Payment window expired", eyebrow: "Expired", accent: "#B34141",
             blurb: "Your reservation window expired before payment completed." };
  }
  if (s === "payment_failed") {
    return { label: "Payment failed", eyebrow: "Payment failed", accent: "#B34141",
             blurb: "Your payment did not go through. Please try again from the workspace page." };
  }
  if (s === "deposit_paid" || s === "balance_processing") {
    return { label: "Deposit received", eyebrow: "Deposit paid", accent: "#B8913A",
             blurb: "Your deposit has been captured. The balance will run automatically before your visit." };
  }
  return { label: state || "Reservation", eyebrow: "Reservation", accent: "#1B2340", blurb: "" };
}

export default function MemberBookingDetail() {
  const { reqId } = useParams();
  const [br, setBr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/bookings/v2/${encodeURIComponent(reqId)}`)
      .then((r) => { if (!cancelled) { setBr(r.data); setError(""); } })
      .catch((e) => {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 404) setError("We couldn't find that booking. It may have been removed.");
        else if (code === 403) setError("You don't have access to this booking.");
        else setError(e?.response?.data?.detail || "Could not load booking.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reqId]);

  const view = useMemo(() => {
    if (!br) return null;
    const pres = statePresentation(br.state);
    const when = fmtDateTime(br.date, br.start_time);
    const payment = br.payment || {};
    return { pres, when, payment };
  }, [br]);

  return (
    <AppShell>
      <div className="rec-page" style={{ background: "transparent" }}>
        <main className="rec-main" data-testid="member-booking-detail">
          <div style={{ marginBottom: 22 }}>
            <Link to="/notifications" data-testid="member-booking-back-notifications" style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--m-muted)", textDecoration: "none" }}>
              ← Back to notifications
            </Link>
          </div>
          <span className="rec-eyebrow">Métier · Reservation</span>
          {loading && (
            <div className="m-loading-inline" data-testid="member-booking-loading">Loading your reservation…</div>
          )}
          {!loading && error && (
            <div className="m-callout" data-testid="member-booking-error" style={{ marginTop: 18 }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, margin: "0 0 6px" }}>We couldn't load this booking.</h2>
              <p style={{ fontSize: 14.5, lineHeight: 1.6 }}>{error}</p>
              <p style={{ fontSize: 13, color: "var(--m-muted)", marginTop: 10 }}>
                Reference: <code>{reqId}</code>. If you need help, please contact{" "}
                <a href="mailto:hello@workmetier.com" data-testid="member-booking-contact-link">hello@workmetier.com</a>.
              </p>
              <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
                <Link to="/spaces" className="m-btn-ghost" data-testid="member-booking-back-spaces">Back to workspaces</Link>
              </div>
            </div>
          )}
          {!loading && !error && view && (
            <>
              <div
                data-testid={`member-booking-state-${String(br.state || "unknown").toLowerCase()}`}
                style={{
                  marginTop: 18, padding: "22px 26px",
                  background: "rgba(184,145,58,0.06)",
                  border: `1px solid ${view.pres.accent}66`,
                  borderRadius: 14,
                }}
              >
                <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: view.pres.accent, fontWeight: 600 }}>
                  {view.pres.eyebrow}
                </div>
                <h1
                  className="rec-h1"
                  data-testid="member-booking-headline"
                  style={{ fontSize: "clamp(28px, 4vw, 40px)", marginTop: 6, marginBottom: 8 }}
                >
                  {br.workspace_name}
                </h1>
                <div style={{ fontSize: 16, lineHeight: 1.55, color: "var(--m-text)" }} data-testid="member-booking-when">
                  {view.when}{br.hours ? ` · ${br.hours}h` : ""}
                </div>
                {view.pres.blurb && (
                  <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "rgba(27,35,64,0.78)", marginTop: 12 }} data-testid="member-booking-blurb">
                    {view.pres.blurb}
                  </p>
                )}
              </div>

              <section className="m-card" style={{ marginTop: 22 }} data-testid="member-booking-summary">
                <h3 className="m-side-title">Reservation <em>summary</em></h3>
                <dl style={{ display: "grid", gridTemplateColumns: "minmax(140px, 200px) 1fr", rowGap: 10, columnGap: 18, fontSize: 14 }}>
                  <dt style={{ color: "var(--m-muted)" }}>Reference</dt>
                  <dd data-testid="member-booking-ref">{br.request_id}</dd>
                  <dt style={{ color: "var(--m-muted)" }}>Workspace</dt>
                  <dd data-testid="member-booking-workspace">{br.workspace_name}</dd>
                  {br.date && (<>
                    <dt style={{ color: "var(--m-muted)" }}>Date</dt>
                    <dd data-testid="member-booking-date">{br.date}</dd>
                  </>)}
                  {br.start_time && (<>
                    <dt style={{ color: "var(--m-muted)" }}>Start</dt>
                    <dd data-testid="member-booking-start">{br.start_time}</dd>
                  </>)}
                  {br.hours != null && (<>
                    <dt style={{ color: "var(--m-muted)" }}>Duration</dt>
                    <dd data-testid="member-booking-duration">{br.hours} hour{br.hours === 1 ? "" : "s"}</dd>
                  </>)}
                  {view.payment.gross_cents != null && (<>
                    <dt style={{ color: "var(--m-muted)" }}>Total</dt>
                    <dd data-testid="member-booking-total">{fmtMoney(view.payment.gross_cents)}</dd>
                  </>)}
                  {view.payment.status && (<>
                    <dt style={{ color: "var(--m-muted)" }}>Payment</dt>
                    <dd data-testid="member-booking-payment-status">{view.payment.status.replace(/_/g, " ")}</dd>
                  </>)}
                  <dt style={{ color: "var(--m-muted)" }}>Status</dt>
                  <dd data-testid="member-booking-state">{view.pres.label}</dd>
                </dl>
                <p style={{ fontSize: 12, lineHeight: 1.55, color: "var(--m-muted)", marginTop: 16 }}>
                  Your booking record is preserved on Métier for accounting and support. Cancellation and refund rules are set by
                  the host and apply per the policy shown at checkout.
                </p>
                <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {br.workspace_slug && (
                    <Link
                      to={`/spaces/${br.workspace_slug}`}
                      className="m-btn-ghost"
                      data-testid="member-booking-view-workspace"
                    >
                      View workspace
                    </Link>
                  )}
                  <a
                    href={`mailto:hello@workmetier.com?subject=${encodeURIComponent(`Booking support - ${br.request_id || reqId}`)}`}
                    className="m-btn-ghost"
                    data-testid="member-booking-contact-support"
                  >
                    Contact support
                  </a>
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </AppShell>
  );
}
